import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Save, Eye, FileText, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/api";

const EMPTY = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  cover_image: "",
  tags: "",
  status: "draft",
  meta_title: "",
  meta_description: "",
};

export default function AdminBlogEditor() {
  const { id } = useParams();
  const isNew = !id || id === "nouveau";
  const navigate = useNavigate();

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [savedSlug, setSavedSlug] = useState(""); // for "View public" link
  const [savedStatus, setSavedStatus] = useState("");

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    adminApi.listBlogPosts()
      .then((posts) => {
        const p = posts.find((x) => x.id === id);
        if (!p) { toast.error("Article introuvable."); navigate("/admin/blog"); return; }
        setForm({
          title: p.title || "",
          slug: p.slug || "",
          excerpt: p.excerpt || "",
          content: p.content || "",
          cover_image: p.cover_image || "",
          tags: (p.tags || []).join(", "),
          status: p.status || "draft",
          meta_title: p.meta_title || "",
          meta_description: p.meta_description || "",
        });
        setSavedSlug(p.slug || "");
        setSavedStatus(p.status || "");
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [id, isNew, navigate]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (publish = null) => {
    if (!form.title || form.title.trim().length < 4) {
      toast.error("Le titre doit faire au moins 4 caractères.");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      content: form.content,
      cover_image: form.cover_image.trim(),
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      meta_title: form.meta_title.trim(),
      meta_description: form.meta_description.trim(),
      status: publish !== null ? publish : form.status,
    };
    if (form.slug && form.slug.trim()) payload.slug = form.slug.trim();
    try {
      let res;
      if (isNew) {
        res = await adminApi.createBlogPost(payload);
        toast.success("Article créé.");
        navigate(`/admin/blog/${res.id}`, { replace: true });
      } else {
        res = await adminApi.updateBlogPost(id, payload);
        toast.success(payload.status === "published" ? "Article publié." : "Article enregistré.");
      }
      setSavedSlug(res.slug || "");
      setSavedStatus(res.status || "");
      setForm((f) => ({ ...f, status: res.status || f.status, slug: res.slug || f.slug }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-white/55 italic">Chargement…</p>;

  return (
    <div data-testid="admin-blog-editor">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link to="/admin/blog" data-testid="admin-blog-back" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-[#DCB457] transition">
            <ArrowLeft size={12} strokeWidth={1.4} /> Tous les articles
          </Link>
          <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">
            {isNew ? "Nouvel" : "Éditer un"} <span className="italic gold-text">article.</span>
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setPreviewing((v) => !v)} data-testid="admin-blog-toggle-preview" className="btn-ghost-gold inline-flex items-center gap-2">
            {previewing ? <FileText size={14} strokeWidth={1.4} /> : <Eye size={14} strokeWidth={1.4} />}
            {previewing ? "Édition" : "Aperçu"}
          </button>
          {savedSlug && savedStatus === "published" && (
            <a href={`/blog/${savedSlug}`} target="_blank" rel="noopener noreferrer" data-testid="admin-blog-view-public" className="btn-ghost-gold inline-flex items-center gap-2">
              <ExternalLink size={13} strokeWidth={1.4} /> Voir en ligne
            </a>
          )}
          <button onClick={() => save("draft")} disabled={saving} data-testid="admin-blog-save-draft" className="btn-ghost-gold inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={1.4} />}
            Brouillon
          </button>
          <button onClick={() => save("published")} disabled={saving} data-testid="admin-blog-publish" className="btn-gold inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} strokeWidth={1.4} />}
            Publier
          </button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {!previewing ? (
            <>
              <FieldLabel label="Titre *">
                <input className="input-luxe" value={form.title} onChange={(e) => update("title", e.target.value)} data-testid="admin-blog-title" placeholder="10 conseils pour réussir l'animation musicale de votre mariage" />
              </FieldLabel>

              <FieldLabel label="Slug (URL) — optionnel">
                <input className="input-luxe font-mono text-xs" value={form.slug} onChange={(e) => update("slug", e.target.value)} data-testid="admin-blog-slug" placeholder="auto-généré si vide — ex. conseils-mariage" />
              </FieldLabel>

              <FieldLabel label="Extrait (résumé court)">
                <textarea rows={3} className="input-luxe resize-none" value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} data-testid="admin-blog-excerpt" placeholder="2-3 phrases qui résument l'article — utilisées pour le SEO et la carte d'aperçu." />
              </FieldLabel>

              <FieldLabel label="Contenu (Markdown)">
                <textarea
                  rows={22}
                  className="input-luxe resize-none font-mono text-xs leading-relaxed"
                  value={form.content}
                  onChange={(e) => update("content", e.target.value)}
                  data-testid="admin-blog-content"
                  placeholder={"# Titre principal\n\nVotre intro…\n\n## Section\n\n- Point 1\n- Point 2\n\n**Texte en gras**, *italique*, [lien](https://exemple.fr)\n\n> Une citation\n\n```\ncode\n```"}
                />
                <p className="text-[11px] text-white/40 mt-2">
                  Supporte la syntaxe Markdown standard (titres, gras, listes, citations, code, liens, tableaux).
                </p>
              </FieldLabel>
            </>
          ) : (
            <div className="card-luxe p-8 prose-blog" data-testid="admin-blog-preview">
              {form.cover_image && (
                <div className="aspect-[16/8] overflow-hidden bg-black/50 mb-8 -m-8 mb-6">
                  <img src={form.cover_image} alt={form.title} className="w-full h-full object-cover" />
                </div>
              )}
              <h1 style={{ fontFamily: "serif" }}>{form.title || "Sans titre"}</h1>
              {form.excerpt && <p style={{ opacity: 0.7 }}><em>{form.excerpt}</em></p>}
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content || "*Aucun contenu pour l'instant.*"}</ReactMarkdown>
            </div>
          )}
        </div>

        <aside className="space-y-5">
          <div className="card-luxe p-5 space-y-4">
            <FieldLabel label="Image de couverture (URL)">
              <input className="input-luxe text-xs" value={form.cover_image} onChange={(e) => update("cover_image", e.target.value)} data-testid="admin-blog-cover" placeholder="https://images.pexels.com/…" />
            </FieldLabel>
            {form.cover_image && (
              <div className="aspect-[16/10] overflow-hidden bg-black/50">
                <img src={form.cover_image} alt="cover" className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          <div className="card-luxe p-5 space-y-4">
            <FieldLabel label="Tags (séparés par virgule)">
              <input className="input-luxe text-xs" value={form.tags} onChange={(e) => update("tags", e.target.value)} data-testid="admin-blog-tags" placeholder="mariage, conseils, playlist" />
            </FieldLabel>

            <FieldLabel label="Statut">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em]">
                <span className={`px-3 py-1.5 border ${form.status === "published" ? "border-[#DCB457]/50 text-[#DCB457]" : "border-white/15 text-white/55"}`}>
                  {form.status === "published" ? "Publié" : "Brouillon"}
                </span>
              </div>
            </FieldLabel>
          </div>

          <div className="card-luxe p-5 space-y-4">
            <p className="eyebrow">SEO</p>
            <FieldLabel label="Meta title (60 car. max recommandé)">
              <input className="input-luxe text-xs" value={form.meta_title} onChange={(e) => update("meta_title", e.target.value)} data-testid="admin-blog-meta-title" placeholder="Vide = titre de l'article" maxLength={180} />
              <p className="text-[10px] text-white/40 mt-1">{(form.meta_title || form.title || "").length} / 60</p>
            </FieldLabel>
            <FieldLabel label="Meta description (160 car. max recommandé)">
              <textarea rows={3} className="input-luxe text-xs resize-none" value={form.meta_description} onChange={(e) => update("meta_description", e.target.value)} data-testid="admin-blog-meta-desc" placeholder="Vide = extrait de l'article" maxLength={300} />
              <p className="text-[10px] text-white/40 mt-1">{(form.meta_description || form.excerpt || "").length} / 160</p>
            </FieldLabel>
          </div>
        </aside>
      </div>
    </div>
  );
}

function FieldLabel({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.2em] text-white/60 mb-2">{label}</span>
      {children}
    </label>
  );
}
